import './styles/_shop-modal.scss';

// eslint-disable-next-line import/no-unresolved
import Swiper from 'swiper-latest/bundle';

import { formatMoney } from '../utils/string';
import { checkIsTablet } from '../utils/dom';
import { initSwipers } from '../utils/swiper';

const state = {
  variables: {
    containerName: 'shop-modal-container',
    handle: null,
    isTablet: checkIsTablet(),
  },
  elements: {
    closeButtonSvg: null,
  },
};

/**
 * Dispatches the 'shop::close' event
 */
export const closeShopModal = () => {
  document.dispatchEvent(new CustomEvent('shop::close'));
};

/**
 * Dispatches the 'product::shop::open' event
 */
export const openShopModal = (target) => {
  const imagesData = target.querySelector('[data-shop-images]');
  const productsData = target.querySelectorAll('[data-product-obj]');

  const { images } = JSON.parse(imagesData?.textContent);
  const products = Array.from(productsData)?.map((product) =>
    JSON.parse(product.textContent)
  );

  document.dispatchEvent(
    new CustomEvent('shop::open', {
      detail: {
        images,
        products,
      },
    })
  );
};

/**
 * Caches DOM elements to avoid multiple lookups
 */
const cacheDOMElements = () => {
  state.elements.closeButtonSvg = document
    .querySelector('.search_btn--close svg')
    ?.cloneNode(true);
};

/**
 * Creates the close button for the quick view modal
 * @returns {HTMLElement} The close button element
 */
const createCloseButton = () => {
  const closeButton = document.createElement('button');
  closeButton.classList.add('shop-modal-close');
  closeButton.setAttribute('data-shop-modal-close', '');

  if (state.elements.closeButtonSvg) {
    closeButton.appendChild(state.elements.closeButtonSvg);
  }

  return closeButton;
};

const createImage = (src, size) => {
  const updatedSrc = typeof src === 'object' ? src.originalSrc : src;
  const imageSrc = updatedSrc.replace(/(\.[^.?]*)(\?.*)?$/, `_x${size}$1$2`);
  return `
    <img src="${imageSrc}" class="lazy-load placeholder-loader" width="${size}" height="${size}" alt="" />
  `;
};

const createImages = (images, swiperDataAttr) => {
  const imagesHtml = Array.from(images)
    .map(
      (image) => `
      <div class="shop-modal-image ${swiperDataAttr ? 'swiper-slide' : ''}">
        ${image}
      </div>
    `
    )
    .join('');

  return swiperDataAttr
    ? `
    <div ${swiperDataAttr} class="shopable-images swiper">
      <div class="swiper-wrapper">
        ${imagesHtml}
      </div>
      <div class="swiper-control swiper-button-next"></div>
      <div class="swiper-control swiper-button-prev"></div>
    </div>
  `
    : imagesHtml;
};

const createProductImages = (product) => {
  if (state.variables.isTablet) {
    return `
      <div data-open-quick-view="${product.handle}" class="shop-modal__product-image">
        ${createImage(product.featured_image, '300')}
      </div>
    `;
  }

  const imagesHtml = product.images.map(
    (image) => `
      <div data-open-quick-view="${product.handle}" class="shop-modal__product-image swiper-slide">
        ${createImage(image, '300')}
      </div>
    `
  );
  return `
    <div data-shop-product-images-swiper class="shopable-product-images swiper">
      <div class="swiper-wrapper">
        ${imagesHtml.join('')}
      </div>
      <div class="swiper-control swiper-button-next"></div>
      <div class="swiper-control swiper-button-prev"></div>
    </div>
  `;
};

const createProducts = (productsObj) => {
  const productsHtml = productsObj
    .map(
      (productObj) => `
      <div class="shop-modal-product">
        ${createProductImages(productObj.product)}
        <div class="shop-modal__product-details">
          <h4 class="shop-modal__product-title">${productObj.product.title}</h4>
          <div class="wishlist-engine" data-product_id="${productObj.product.id}" data-variant_id="${productObj.product.variants[0].id}" data-full_button="false" data-css="true"></div>
        </div>
        <p class="shop-modal__product-price">${formatMoney(productObj.product.variants[0].price)}</p>
      </div>
    `
    )
    .join('');

  return `
    <div class="shop-modal-products">
      ${productsHtml}
    </div>
  `;
};

const initializeSwiper = (el) =>
  new Swiper(el, {
    spaceBetween: 0,
    lazy: true,
    navigation: {
      nextEl: el.querySelector('.swiper-button-next'),
      prevEl: el.querySelector('.swiper-button-prev'),
    },
  });

/**
 * Handles the opening of the quick view modal
 * @param {Event} e - The event object containing details for the quick view
 */
const handleShopModalOpen = (e) => {
  const { images, products } = e.detail;
  let shopModalContainer = document.querySelector(
    `.${state.variables.containerName}`
  );

  const imagesHTML = createImages(images, 'data-shop-images-swiper');
  const productsHTML = createProducts(products);
  const content = imagesHTML + productsHTML;

  if (!shopModalContainer) {
    shopModalContainer = createShopModalContainer(content);
    document.body.appendChild(shopModalContainer);
  } else {
    shopModalContainer.querySelector('.shop-modal-content').innerHTML = content;
  }

  if (state.variables.isTablet) {
    initializeSwiper(
      shopModalContainer.querySelector('[data-shop-images-swiper]')
    );
  } else {
    initSwipers(
      shopModalContainer.querySelectorAll('[data-shop-product-images-swiper]'),
      initializeSwiper,
      true
    );
  }

  showShopModalContainer(shopModalContainer);
};

/**
 * Creates the quick view container and inserts content
 * @param {string} contentHTML - The HTML content to be displayed in the quick view
 * @returns {HTMLElement} The quick view container element
 */
const createShopModalContainer = (contentHTML) => {
  const shopModalContainer = document.createElement('div');
  shopModalContainer.classList.add(state.variables.containerName);
  shopModalContainer.innerHTML = `
    ${createCloseButton().outerHTML}
    <div class="shop-modal-content">${contentHTML}</div>
  `;
  return shopModalContainer;
};

/**
 * Shows the quick view container and locks body scroll
 * @param {HTMLElement} container - The quick view container element
 */
const showShopModalContainer = (container) => {
  container.classList.add('show');
  document.body.classList.add('overflow-hidden--shop-modal');
};

/**
 * Hides and optionally clears the quick view container
 * @param {boolean} clearContent - If true, the content will be removed from the DOM
 */
const handleShopModalClose = (clearContent = false) => {
  const shopModalContainer = document.querySelector(
    `.${state.variables.containerName}`
  );
  if (!shopModalContainer) return;

  shopModalContainer.classList.add('closing');
  setTimeout(() => {
    shopModalContainer.classList.remove('closing');
    document.body.classList.remove('overflow-hidden--shop-modal');
    shopModalContainer.classList.remove('show');
    if (clearContent) {
      shopModalContainer.remove();
    }
  }, 300);
};

/**
 * Handles click events on the quick view container backdrop
 * @param {Event} e - The click event
 */
const handleBackdropClick = (e) => {
  const { target } = e;
  const isShopModalOpenTarget = target.closest('[data-shop-modal-open]');
  if (
    target.classList.contains(state.variables.containerName) ||
    target.closest('[data-shop-modal-close]')
  ) {
    closeShopModal();
    return;
  }
  if (isShopModalOpenTarget) {
    openShopModal(isShopModalOpenTarget);
  }
};

/**
 * Adds event listeners for the quick view functionality
 */
const addEventListeners = () => {
  document.addEventListener('shop::open', handleShopModalOpen);
  document.addEventListener('shop::close', () => handleShopModalClose());
  document.addEventListener('click', handleBackdropClick);
};

/**
 * Initializes the product quick view module
 */
const initShopModal = () => {
  cacheDOMElements();
  addEventListeners();
};

export default initShopModal;
